import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  SpinnerGap,
  Plus,
  Trash,
  FloppyDisk,
  RoadHorizon,
  Warning,
  Folders,
  CloudArrowUp
} from '@phosphor-icons/react';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminContentPage = () => {
  const [activeTab, setActiveTab] = useState('categories');

  return (
    <div className="p-6 md:p-8" data-testid="admin-content">
      <div className="mb-6">
        <h1 className="font-heading font-black text-3xl tracking-tighter">Content Management</h1>
        <p className="text-muted-foreground">Manage categories, road signs, and hazard perception content</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-border">
        {[
          { id: 'categories', label: 'Categories', icon: Folders },
          { id: 'road-signs', label: 'Road Signs', icon: RoadHorizon },
          { id: 'hazard', label: 'Hazard Perception', icon: Warning },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors",
              activeTab === tab.id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'categories' && <CategoriesSection />}
      {activeTab === 'road-signs' && <RoadSignsSection />}
      {activeTab === 'hazard' && <HazardPerceptionSection />}
    </div>
  );
};

const CategoriesSection = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState({ id: '', name: '', icon: '' });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/categories`, { withCredentials: true });
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await axios.post(`${API}/admin/categories`, newCategory, { withCredentials: true });
      setNewCategory({ id: '', name: '', icon: '' });
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add category');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Delete this category? Only empty categories can be deleted.')) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, { withCredentials: true });
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><SpinnerGap className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Add Category */}
      <div className="bg-card border border-border rounded-sm p-4">
        <h3 className="font-bold mb-4">Add New Category</h3>
        <form onSubmit={handleAdd} className="flex gap-4 items-end">
          <div className="flex-1">
            <Label>ID (lowercase, no spaces)</Label>
            <Input
              value={newCategory.id}
              onChange={(e) => setNewCategory({ ...newCategory, id: e.target.value.toLowerCase().replace(/\s/g, '-') })}
              placeholder="e.g., highway-code"
              className="mt-1 rounded-sm"
              required
            />
          </div>
          <div className="flex-1">
            <Label>Name</Label>
            <Input
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              placeholder="e.g., Highway Code"
              className="mt-1 rounded-sm"
              required
            />
          </div>
          <div className="w-32">
            <Label>Icon</Label>
            <Input
              value={newCategory.icon}
              onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
              placeholder="e.g., Book"
              className="mt-1 rounded-sm"
              required
            />
          </div>
          <Button type="submit" disabled={adding} className="rounded-sm">
            {adding ? <SpinnerGap className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </form>
      </div>

      {/* Categories List */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">ID</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Name</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Icon</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Questions</th>
              <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {categories.map(cat => (
              <tr key={cat.id} className="hover:bg-muted/50">
                <td className="p-4 font-mono text-sm">{cat.id}</td>
                <td className="p-4">{cat.name}</td>
                <td className="p-4 text-muted-foreground">{cat.icon}</td>
                <td className="p-4">{cat.question_count}</td>
                <td className="p-4 text-right">
                  <button
                    onClick={() => handleDelete(cat.id)}
                    disabled={cat.question_count > 0}
                    className={cn(
                      "p-2 rounded-sm transition-colors",
                      cat.question_count > 0 
                        ? "text-muted-foreground/30 cursor-not-allowed"
                        : "hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                    )}
                    title={cat.question_count > 0 ? "Cannot delete - has questions" : "Delete category"}
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RoadSignsSection = () => {
  const [signs, setSigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSigns();
  }, []);

  const fetchSigns = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/content/road-signs`, { withCredentials: true });
      setSigns(data);
    } catch (error) {
      console.error('Failed to fetch road signs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/road-signs`, signs, { withCredentials: true });
      alert('Road signs updated successfully');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addCategory = () => {
    setSigns([...signs, { category: 'New Category', signs: [] }]);
  };

  const addSign = (catIndex) => {
    const newSigns = [...signs];
    newSigns[catIndex].signs.push({ name: '', description: '', shape: '', color: '' });
    setSigns(newSigns);
  };

  const updateCategory = (index, value) => {
    const newSigns = [...signs];
    newSigns[index].category = value;
    setSigns(newSigns);
  };

  const updateSign = (catIndex, signIndex, field, value) => {
    const newSigns = [...signs];
    newSigns[catIndex].signs[signIndex][field] = value;
    setSigns(newSigns);
  };

  const removeSign = (catIndex, signIndex) => {
    const newSigns = [...signs];
    newSigns[catIndex].signs.splice(signIndex, 1);
    setSigns(newSigns);
  };

  const removeCategory = (catIndex) => {
    if (!window.confirm('Remove this category and all its signs?')) return;
    const newSigns = [...signs];
    newSigns.splice(catIndex, 1);
    setSigns(newSigns);
  };

  if (loading) {
    return <div className="flex justify-center p-8"><SpinnerGap className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Button onClick={addCategory} variant="outline" className="rounded-sm">
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </Button>
        <Button onClick={handleSave} disabled={saving} className="rounded-sm">
          {saving ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <FloppyDisk className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {signs.map((cat, catIndex) => (
        <div key={catIndex} className="bg-card border border-border rounded-sm p-4">
          <div className="flex items-center gap-4 mb-4">
            <Input
              value={cat.category}
              onChange={(e) => updateCategory(catIndex, e.target.value)}
              className="flex-1 rounded-sm font-bold"
            />
            <Button variant="outline" size="sm" onClick={() => addSign(catIndex)} className="rounded-sm">
              <Plus className="w-4 h-4" />
            </Button>
            <button onClick={() => removeCategory(catIndex)} className="p-2 hover:bg-destructive/10 rounded-sm text-destructive">
              <Trash className="w-4 h-4" />
            </button>
          </div>
          
          <div className="space-y-2">
            {cat.signs.map((sign, signIndex) => (
              <div key={signIndex} className="grid grid-cols-5 gap-2 items-center">
                <Input
                  value={sign.name}
                  onChange={(e) => updateSign(catIndex, signIndex, 'name', e.target.value)}
                  placeholder="Name"
                  className="rounded-sm"
                />
                <Input
                  value={sign.description}
                  onChange={(e) => updateSign(catIndex, signIndex, 'description', e.target.value)}
                  placeholder="Description"
                  className="col-span-2 rounded-sm"
                />
                <Input
                  value={sign.shape}
                  onChange={(e) => updateSign(catIndex, signIndex, 'shape', e.target.value)}
                  placeholder="Shape"
                  className="rounded-sm"
                />
                <div className="flex gap-2">
                  <Input
                    value={sign.color}
                    onChange={(e) => updateSign(catIndex, signIndex, 'color', e.target.value)}
                    placeholder="Color"
                    className="rounded-sm"
                  />
                  <button onClick={() => removeSign(catIndex, signIndex)} className="p-2 hover:bg-destructive/10 rounded-sm text-destructive">
                    <Trash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const HazardPerceptionSection = () => {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClip, setEditingClip] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [msg, setMsg] = useState(null);

  const emptyForm = {
    title: '', description: '', video_url: '', thumbnail_url: '',
    duration: '', hazards: [{ start_time: '', end_time: '', description: '' }],
    order: 0, is_active: true
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchClips(); }, []);

  const fetchClips = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/hazard-clips`);
      setClips(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleUploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(`Uploading ${file.name}...`);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'hazard-clips');
      const { data } = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(p => ({ ...p, video_url: data.url }));
      setUploadProgress('Video uploaded');
    } catch (err) {
      setUploadProgress('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadThumbnail = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'hazard-thumbnails');
      const { data } = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(p => ({ ...p, thumbnail_url: data.url }));
    } catch (err) {
      console.error(err);
    }
  };

  const addHazardWindow = () => {
    setForm(p => ({ ...p, hazards: [...p.hazards, { start_time: '', end_time: '', description: '' }] }));
  };

  const removeHazardWindow = (idx) => {
    setForm(p => ({ ...p, hazards: p.hazards.filter((_, i) => i !== idx) }));
  };

  const updateHazardWindow = (idx, field, value) => {
    setForm(p => ({
      ...p,
      hazards: p.hazards.map((h, i) => i === idx ? { ...h, [field]: value } : h)
    }));
  };

  const handleSave = async () => {
    if (!form.title || !form.video_url || form.hazards.length === 0) return;
    setSaving(true);
    setMsg(null);
    try {
      const payload = {
        ...form,
        duration: form.duration ? parseFloat(form.duration) : null,
        order: parseInt(form.order) || 0,
        hazards: form.hazards.map(h => ({
          start_time: parseFloat(h.start_time) || 0,
          end_time: parseFloat(h.end_time) || 0,
          description: h.description || '',
        })),
      };

      if (editingClip) {
        await axios.put(`${API}/admin/hazard-clips/${editingClip}`, payload);
        setMsg({ type: 'success', text: 'Clip updated.' });
      } else {
        await axios.post(`${API}/admin/hazard-clips`, payload);
        setMsg({ type: 'success', text: 'Clip created.' });
      }
      setShowForm(false);
      setEditingClip(null);
      setForm(emptyForm);
      fetchClips();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to save clip.' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (clip) => {
    setForm({
      title: clip.title || '',
      description: clip.description || '',
      video_url: clip.video_url || '',
      thumbnail_url: clip.thumbnail_url || '',
      duration: clip.duration?.toString() || '',
      hazards: (clip.hazards || []).map(h => ({
        start_time: h.start_time?.toString() || '',
        end_time: h.end_time?.toString() || '',
        description: h.description || '',
      })),
      order: clip.order || 0,
      is_active: clip.is_active !== false,
    });
    setEditingClip(clip.clip_id);
    setShowForm(true);
  };

  const handleDelete = async (clipId) => {
    if (!window.confirm('Delete this clip?')) return;
    try {
      await axios.delete(`${API}/admin/hazard-clips/${clipId}`);
      fetchClips();
    } catch (err) { console.error(err); }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><SpinnerGap className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-hazard-section">
      {msg && (
        <div className={cn(
          "p-3 rounded-sm border flex items-center gap-2 text-sm",
          msg.type === 'success' ? "bg-success/10 border-success/30 text-success" : "bg-destructive/10 border-destructive/30 text-destructive"
        )}>
          {msg.text}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end">
        <Button onClick={() => { setShowForm(!showForm); setEditingClip(null); setForm(emptyForm); }} className="rounded-sm" data-testid="add-clip-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Clip
        </Button>
      </div>

      {/* Clip Form */}
      {showForm && (
        <div className="bg-card border border-border rounded-sm p-6 space-y-4" data-testid="clip-form">
          <h3 className="font-bold text-lg">{editingClip ? 'Edit Clip' : 'New Hazard Clip'}</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Title</Label>
              <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))}
                placeholder="e.g. Residential Street — Pedestrian" className="rounded-sm mt-1" data-testid="clip-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Duration (sec)</Label>
                <Input type="number" value={form.duration} onChange={e => setForm(p => ({...p, duration: e.target.value}))}
                  placeholder="60" className="rounded-sm mt-1" data-testid="clip-duration-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Order</Label>
                <Input type="number" value={form.order} onChange={e => setForm(p => ({...p, order: parseInt(e.target.value) || 0}))}
                  className="rounded-sm mt-1" />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Description</Label>
            <Input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))}
              placeholder="Optional description" className="rounded-sm mt-1" />
          </div>

          {/* Video Upload */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Video</Label>
            <div className="flex gap-2 mt-1">
              <Input value={form.video_url} onChange={e => setForm(p => ({...p, video_url: e.target.value}))}
                placeholder="Video URL or upload below" className="rounded-sm flex-1 font-mono text-xs" data-testid="clip-video-url" />
              <div className="relative">
                <Button variant="outline" className="rounded-sm" disabled={uploading} asChild>
                  <label className="cursor-pointer" data-testid="upload-video-btn">
                    {uploading ? <SpinnerGap className="w-4 h-4 animate-spin mr-1" /> : <CloudArrowUp className="w-4 h-4 mr-1" />}
                    Upload
                    <input type="file" accept="video/*" onChange={handleUploadVideo} className="hidden" />
                  </label>
                </Button>
              </div>
            </div>
            {uploadProgress && <p className="text-xs text-muted-foreground mt-1">{uploadProgress}</p>}
          </div>

          {/* Thumbnail Upload */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Thumbnail (optional)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={form.thumbnail_url} onChange={e => setForm(p => ({...p, thumbnail_url: e.target.value}))}
                placeholder="Thumbnail URL" className="rounded-sm flex-1 font-mono text-xs" />
              <div className="relative">
                <Button variant="outline" className="rounded-sm" asChild>
                  <label className="cursor-pointer">
                    <CloudArrowUp className="w-4 h-4 mr-1" />
                    Upload
                    <input type="file" accept="image/*" onChange={handleUploadThumbnail} className="hidden" />
                  </label>
                </Button>
              </div>
            </div>
          </div>

          {/* Hazard Windows */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Hazard Windows</Label>
              <Button variant="outline" size="sm" onClick={addHazardWindow} className="rounded-sm" data-testid="add-hazard-window">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {form.hazards.map((h, idx) => (
                <div key={idx} className="flex gap-2 items-center bg-muted/50 rounded-sm p-2">
                  <span className="text-xs text-muted-foreground w-6">#{idx + 1}</span>
                  <Input type="number" step="0.1" value={h.start_time} onChange={e => updateHazardWindow(idx, 'start_time', e.target.value)}
                    placeholder="Start (s)" className="rounded-sm w-24 text-sm" data-testid={`hazard-start-${idx}`} />
                  <span className="text-muted-foreground text-xs">to</span>
                  <Input type="number" step="0.1" value={h.end_time} onChange={e => updateHazardWindow(idx, 'end_time', e.target.value)}
                    placeholder="End (s)" className="rounded-sm w-24 text-sm" data-testid={`hazard-end-${idx}`} />
                  <Input value={h.description} onChange={e => updateHazardWindow(idx, 'description', e.target.value)}
                    placeholder="Description" className="rounded-sm flex-1 text-sm" />
                  {form.hazards.length > 1 && (
                    <button onClick={() => removeHazardWindow(idx)} className="p-1 text-destructive hover:bg-destructive/10 rounded-sm">
                      <Trash className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({...p, is_active: e.target.checked}))}
              className="rounded" id="clip-active" />
            <Label htmlFor="clip-active" className="text-sm cursor-pointer">Active (visible to users)</Label>
          </div>

          <div className="flex gap-3">
            <Button onClick={handleSave} disabled={saving || !form.title || !form.video_url} className="rounded-sm" data-testid="save-clip-btn">
              {saving ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <FloppyDisk className="w-4 h-4 mr-2" />}
              {editingClip ? 'Update Clip' : 'Create Clip'}
            </Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditingClip(null); }} className="rounded-sm">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Clips List */}
      {clips.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="no-clips-admin">
          <Warning className="w-8 h-8 mx-auto mb-3" />
          <p className="text-sm">No hazard clips yet. Add one above.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="clips-list">
          {clips.map((clip) => (
            <div key={clip.clip_id} className="bg-card border border-border rounded-sm p-4 flex items-center justify-between" data-testid={`admin-clip-${clip.clip_id}`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-9 bg-black rounded-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {clip.thumbnail_url ? (
                    <img src={clip.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Warning className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <p className="font-bold text-sm">{clip.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {clip.hazards?.length || 0} hazard{(clip.hazards?.length || 0) !== 1 ? 's' : ''}
                    {clip.duration ? ` • ${Math.round(clip.duration)}s` : ''}
                    <span className={cn("ml-2 font-bold", clip.is_active ? "text-success" : "text-muted-foreground")}>
                      {clip.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(clip)} className="rounded-sm" data-testid={`edit-clip-${clip.clip_id}`}>
                  Edit
                </Button>
                <button onClick={() => handleDelete(clip.clip_id)} className="p-2 hover:bg-destructive/10 rounded-sm text-destructive" data-testid={`delete-clip-${clip.clip_id}`}>
                  <Trash className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
